/**
 * PublicInvoicePaymentView
 *
 * Public (no-auth) page reached from the tokenised "Pay Now" link in an
 * invoice email: /invoice/pay/:token. Shows the invoice summary and routes
 * the member to Stripe Checkout. Honours ?status=cancelled to show a retry
 * message after a cancelled/abandoned payment.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import invoiceService from '../../services/invoiceService';
import './PublicInvoicePayment.css';

const PublicInvoicePaymentView = () => {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const wasCancelled = searchParams.get('status') === 'cancelled';

  const [loading, setLoading] = useState(true);
  const [invoice, setInvoice] = useState(null);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [paypalSubmitting, setPaypalSubmitting] = useState(false);
  const [payError, setPayError] = useState(null);

  const loadInvoice = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await invoiceService.getPublicInvoice(token);
      if (res.success && res.data?.data) {
        setInvoice(res.data.data);
      } else {
        setError(res.data?.message || res.message || 'This payment link is invalid or has expired.');
      }
    } catch (err) {
      setError('Unable to load invoice. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadInvoice();
  }, [loadInvoice]);

  const handlePayNow = async () => {
    setSubmitting(true);
    setPayError(null);
    try {
      const res = await invoiceService.createCheckoutSession(token);
      const checkoutUrl = res.data?.data?.checkout_url;
      if (res.success && checkoutUrl) {
        // Redirect to Stripe's hosted checkout page.
        window.location.href = checkoutUrl;
        return;
      }
      setPayError(res.data?.message || res.message || 'Unable to start payment. Please try again.');
    } catch (err) {
      setPayError('Unable to start payment. Please try again later.');
    } finally {
      setSubmitting(false);
    }
  };

  const handlePayWithPayPal = async () => {
    setPaypalSubmitting(true);
    setPayError(null);
    try {
      const res = await invoiceService.createPaypalOrder(token);
      const approveUrl = res.data?.data?.approve_url;
      if (res.success && approveUrl) {
        // Redirect to PayPal's hosted approval page.
        window.location.href = approveUrl;
        return;
      }
      setPayError(res.data?.message || res.message || 'Unable to start PayPal payment. Please try again.');
    } catch (err) {
      setPayError('Unable to start PayPal payment. Please try again later.');
    } finally {
      setPaypalSubmitting(false);
    }
  };

  const formatAmount = (amount, currency) => {
    const value = Number(amount || 0);
    try {
      return new Intl.NumberFormat('en-AU', {
        style: 'currency',
        currency: (currency || 'aud').toUpperCase(),
      }).format(value);
    } catch (e) {
      return `$${value.toFixed(2)}`;
    }
  };

  if (loading) {
    return (
      <div className="pip-container">
        <div className="pip-card pip-center">Loading invoice…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="pip-container">
        <div className="pip-card pip-center">
          <h1 className="pip-title">Payment link unavailable</h1>
          <p className="pip-muted">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="pip-container">
      <div className="pip-card">
        <h1 className="pip-title">Invoice {invoice?.invoice_number || 'N/A'}</h1>
        {invoice?.organization_name ? (
          <p className="pip-muted">{invoice.organization_name}</p>
        ) : null}

        <div className="pip-amount-row">
          <span>Amount due</span>
          <span className="pip-amount">{formatAmount(invoice?.amount || 0, invoice?.currency || 'AUD')}</span>
        </div>

        {invoice?.is_paid ? (
          <div className="pip-banner pip-banner-success">
            This invoice has already been paid. No further action is required.
          </div>
        ) : (
          <>
            {wasCancelled ? (
              <div className="pip-banner pip-banner-warn">
                Your payment was cancelled. You have not been charged — you can try again below.
              </div>
            ) : null}

            {payError ? (
              <div className="pip-banner pip-banner-error">{payError}</div>
            ) : null}

            {invoice?.is_payable ? (
              (() => {
                const options = invoice?.payment_options || {};
                const anyOption = options.stripe || options.paypal;
                if (!anyOption) {
                  return (
                    <div className="pip-banner pip-banner-warn">
                      Online payment is not available at this time. Please contact us for assistance.
                    </div>
                  );
                }
                const busy = submitting || paypalSubmitting;
                return (
                  <>
                    {options.stripe ? (
                      <button className="pip-pay-button" onClick={handlePayNow} disabled={busy}>
                        {submitting ? 'Redirecting to secure checkout…' : 'Pay with Card (Stripe)'}
                      </button>
                    ) : null}
                    {options.paypal ? (
                      <button
                        className="pip-pay-button pip-pay-button-paypal"
                        onClick={handlePayWithPayPal}
                        disabled={busy}
                      >
                        {paypalSubmitting ? 'Redirecting to PayPal…' : 'Pay with PayPal'}
                      </button>
                    ) : null}
                  </>
                );
              })()
            ) : (
              <div className="pip-banner pip-banner-warn">
                This invoice is not currently payable online. Please contact us for assistance.
              </div>
            )}
            <p className="pip-secure-note">🔒 Payments are processed securely by Stripe and PayPal.</p>
          </>
        )}
      </div>
    </div>
  );
};

export default PublicInvoicePaymentView;
